import React, { useEffect, useState } from 'react';
import { apiCall } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';

interface InterestedParty {
  id: string;
  patient_id: string;
  name: string;
  role?: string;
  organisation?: string;
  phone?: string;
  email?: string;
  notes?: string;
  is_active: boolean;
}

interface FormDataState {
  name: string;
  role: string;
  organisation: string;
  phone: string;
  email: string;
  notes: string;
}

const EMPTY_FORM: FormDataState = { name: '', role: '', organisation: '', phone: '', email: '', notes: '' };

interface InterestedPartiesTabProps {
  patientId: string;
}

/**
 * Full "Interested Parties" management view - other clinicians/allied
 * health involved in a patient's care beyond the primary referrer. Shared
 * between Patient Details' own tab and the Consult screen's tab so the two
 * never diverge.
 */
const InterestedPartiesTab: React.FC<InterestedPartiesTabProps> = ({ patientId }) => {
  const { token } = useAuth();
  const [parties, setParties] = useState<InterestedParty[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormDataState>(EMPTY_FORM);
  const [error, setError] = useState('');

  const loadParties = async () => {
    if (!patientId || !token) return;
    setLoading(true);
    try {
      const response = await apiCall(`/patients/interested-parties/?patient_id=${patientId}`, { method: 'GET' }, token);
      if (response.ok) {
        const data = await response.json();
        setParties(data.results || data || []);
      }
    } catch (err) {
      console.error('Error loading interested parties:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadParties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, token]);

  const startAdd = () => {
    setEditingId(null);
    setFormData(EMPTY_FORM);
    setError('');
    setShowForm(true);
  };

  const startEdit = (party: InterestedParty) => {
    setEditingId(party.id);
    setFormData({
      name: party.name,
      role: party.role || '',
      organisation: party.organisation || '',
      phone: party.phone || '',
      email: party.email || '',
      notes: party.notes || '',
    });
    setError('');
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }
    if (!token) return;

    const payload = { ...formData, patient_id: patientId };
    try {
      const response = editingId
        ? await apiCall(`/patients/interested-parties/${editingId}/`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          }, token)
        : await apiCall('/patients/interested-parties/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          }, token);

      if (response.ok) {
        setShowForm(false);
        setEditingId(null);
        setFormData(EMPTY_FORM);
        loadParties();
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.detail || 'Failed to save');
      }
    } catch (err) {
      console.error('Error saving interested party:', err);
      setError('Failed to save');
    }
  };

  const handleRemove = async (party: InterestedParty) => {
    if (!token) return;
    if (!confirm(`Remove ${party.name} from this patient's interested parties?`)) return;
    try {
      const response = await apiCall(`/patients/interested-parties/${party.id}/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: false }),
      }, token);
      if (response.ok) loadParties();
    } catch (err) {
      console.error('Error removing interested party:', err);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-base-100 rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">Interested Parties</h3>
          <button className="btn btn-primary btn-sm" onClick={startAdd}>
            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            Add Interested Party
          </button>
        </div>
        <p className="text-sm text-base-content opacity-60 mb-4">
          Other clinicians or parties involved in this patient's clinical care beyond the primary referrer -
          a co-managing specialist, allied health provider, case manager, family GP, etc.
        </p>

        {loading ? (
          <p className="text-base-content opacity-60">Loading...</p>
        ) : parties.length > 0 ? (
          <div className="space-y-3">
            {parties.map((party) => (
              <div key={party.id} className="border border-accent rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium">{party.name}</p>
                    {(party.role || party.organisation) && (
                      <p className="text-sm opacity-70">
                        {[party.role, party.organisation].filter(Boolean).join(' - ')}
                      </p>
                    )}
                    {(party.phone || party.email) && (
                      <p className="text-sm opacity-70 mt-1">
                        {[party.phone, party.email].filter(Boolean).join(' | ')}
                      </p>
                    )}
                    {party.notes && <p className="text-sm opacity-60 mt-1">{party.notes}</p>}
                  </div>
                  <div className="flex gap-2">
                    <button className="text-sm text-blue-700 hover:text-blue-800" onClick={() => startEdit(party)}>
                      Edit
                    </button>
                    <button className="text-sm text-red-700 hover:text-red-800" onClick={() => handleRemove(party)}>
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-base-content opacity-60">No interested parties recorded</p>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-base-100 rounded-lg shadow-xl w-full max-w-lg p-6">
            <h3 className="text-lg font-semibold mb-4">{editingId ? 'Edit' : 'Add'} Interested Party</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && <div className="alert alert-error text-sm">{error}</div>}
              <div className="form-control">
                <label className="label"><span className="label-text">Name *</span></label>
                <input
                  type="text"
                  className="input input-bordered w-full"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="form-control">
                  <label className="label"><span className="label-text">Role</span></label>
                  <input
                    type="text"
                    className="input input-bordered w-full"
                    placeholder="e.g. Co-managing Specialist"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  />
                </div>
                <div className="form-control">
                  <label className="label"><span className="label-text">Organisation</span></label>
                  <input
                    type="text"
                    className="input input-bordered w-full"
                    value={formData.organisation}
                    onChange={(e) => setFormData({ ...formData, organisation: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="form-control">
                  <label className="label"><span className="label-text">Phone</span></label>
                  <input
                    type="text"
                    className="input input-bordered w-full"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
                <div className="form-control">
                  <label className="label"><span className="label-text">Email</span></label>
                  <input
                    type="email"
                    className="input input-bordered w-full"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-control">
                <label className="label"><span className="label-text">Notes</span></label>
                <textarea
                  className="textarea textarea-bordered w-full"
                  rows={2}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" className="btn btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">{editingId ? 'Save' : 'Add'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterestedPartiesTab;
